import { useDataPart } from '@ai-sdk-tools/store';
import type { BrowserStepDataPart } from '@newfront-insurance/ai-api';
import type { ToolUIPart } from 'ai';
import { useCallback, useState } from 'react';

import { useBrowserPreviewSafe } from './browser-preview/browser-preview-provider';
import { renderToolPendingState } from './tool-pending-state';
import {
  BROWSER_LOGIN_HANDOFF_TOOL_NAME,
  isBrowserLoginHandoffToolPart,
  isResolvedBrowserLoginHandoff,
  type BrowserLoginHandoffToolPart,
} from '../../../../lib/browser-login-handoff';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useToolHandoffContinuation } from '../../hooks/use-tool-handoff-continuation';

export function BrowserLoginHandoffTool({ part }: { part: ToolUIPart; messageUuid: string }) {
  if (!isBrowserLoginHandoffToolPart(part)) return null;
  return <BrowserLoginHandoffCard part={part} />;
}

function BrowserLoginHandoffCard({ part }: { part: BrowserLoginHandoffToolPart }) {
  const { isReadOnly, isPublicMode } = useChatOverlayContext();
  const browserPreview = useBrowserPreviewSafe();
  const { submitToolHandoffContinuation } = useToolHandoffContinuation();
  const [rememberSignIn, setRememberSignIn] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const handoffInput = part.input;
  const handoffId = handoffInput?.handoffId;
  const [latestStep] = useDataPart<(BrowserStepDataPart & { handoffId?: string }) | undefined>('browserStep');
  // The handoff step is emitted with the already-connected DCV URL. Use it
  // instead of making the sign-in card wait on a second live-view request.
  const handoffStep = latestStep?.handoffId === handoffId ? latestStep : undefined;

  const openSecureBrowser = useCallback(() => {
    if (!handoffId || !handoffInput) return;
    browserPreview?.open({
      handoffId,
      ...(handoffStep?.sessionViewerUrl
        ? { liveViewUrl: handoffStep.sessionViewerUrl, viewerType: handoffStep.viewerType }
        : {}),
      subtitle: handoffInput.currentUrl,
      title: `Sign in to ${handoffInput.siteName}`,
    });
  }, [browserPreview, handoffId, handoffInput, handoffStep]);

  const submit = useCallback(
    async (action: 'continue' | 'cancel') => {
      setPending(true);
      setError(undefined);
      const output = action === 'continue' ? { action, rememberSignIn } : { action };
      const continued = await submitToolHandoffContinuation({
        tool: BROWSER_LOGIN_HANDOFF_TOOL_NAME,
        toolCallId: part.toolCallId,
        output,
      });
      if (!continued) setError("We couldn't continue the browser task. Please try again.");
      setPending(false);
    },
    [part.toolCallId, rememberSignIn, submitToolHandoffContinuation],
  );

  if (part.state === 'input-streaming') return renderToolPendingState(BROWSER_LOGIN_HANDOFF_TOOL_NAME);
  if (part.state === 'output-available' && isResolvedBrowserLoginHandoff(part.output)) {
    return (
      <div className="my-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
        {part.output.action === 'continue'
          ? 'Sign-in complete — Nova is resuming the browser task.'
          : 'Browser sign-in cancelled.'}
      </div>
    );
  }
  if (part.state !== 'input-available') return null;

  const disabled = pending || isReadOnly || isPublicMode === true;
  return (
    <div className="my-3 mr-auto max-w-full">
      <div className="bg-muted flex max-w-full flex-col gap-4 rounded-[20px] rounded-bl-none px-5 py-4 text-sm">
        <div className="space-y-1">
          <div className="text-steel-500 text-[14px] leading-5 font-semibold">Sign in to {part.input.siteName}</div>
          <p className="text-steel-400 text-[12px] leading-4">
            Open the secure browser to finish signing in. Nova cannot see or enter your credentials.
          </p>
        </div>
        <label className="text-steel-500 flex items-center gap-2 text-[12px] leading-4">
          <input
            type="checkbox"
            checked={rememberSignIn}
            disabled={disabled}
            onChange={(event) => setRememberSignIn(event.target.checked)}
            className="border-steel-200 text-brand-400 size-[18px] rounded-[4px] bg-white"
          />
          <span>Remember this sign-in for future {part.input.siteName} browser tasks on this account.</span>
        </label>
        {error ? <div className="text-fire-500 text-[12px] leading-4">{error}</div> : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled || !handoffId || !browserPreview}
            onClick={openSecureBrowser}
            className="bg-brand-400 hover:bg-brand-300 rounded-[4px] px-3 py-2 text-[14px] leading-5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Open secure browser
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => submit('continue')}
            className="border-steel-200 text-steel-500 hover:bg-steel-50 rounded-[4px] border bg-white px-3 py-2 text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Continuing…' : 'I’m signed in — continue'}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => submit('cancel')}
            className="border-steel-200 text-steel-500 hover:bg-steel-50 rounded-[4px] border bg-white px-3 py-2 text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
